/**
 * VP Product Carousel
 * Converts .vp-collection-product-cards into a Swiper slider.
 * Each .vp-product-card-sm becomes a slide.
 */

const CARD_WIDTH = 306; // 306px card + 16px gap

/**
 * @param {HTMLElement} slider
 * @param {{ (swiperInstance: any): void; (arg0: any): void; }} callback
 */
function waitForSwiper(slider, callback, timeout = 5000) {
  const start = Date.now();

  function check() {
    if (slider.swiper) {
      callback(slider.swiper);
    } else if (Date.now() - start < timeout) {
      requestAnimationFrame(check);
    }
  }

  check();
}
/**
 * Builds a Swiper slider from a container of product cards
 * @param {HTMLElement} container
 * @param {string} sliderClass
 * @param {string} navIdPrefix
 * @returns {HTMLElement|null}
 */
function buildBannerCollectionSwiper(container, sliderClass, navIdPrefix) {
  const cardCount = container.children.length;
  if (cardCount === 0) return null;

  const sectionRoot = container.closest("[id^='shopify-section-']");
  const sectionId = sectionRoot?.id?.replace("shopify-section-", "") || "";

  const swiperSlider = document.createElement("swiper-slider");
  swiperSlider.setAttribute("data-swiper-id", "countdown-collection-products-carousel");
  swiperSlider.className = sliderClass;
  swiperSlider.dataset.swiperConfig = JSON.stringify({
    navigation: {
      enabled: true,
      nextEl: sectionId ? `#${navIdPrefix}-next-${sectionId}` : `[data-${navIdPrefix}-next]`,
      prevEl: sectionId ? `#${navIdPrefix}-prev-${sectionId}` : `[data-${navIdPrefix}-prev]`,
    },
    pagination: { enabled: false },
  });

  const swiperEl = document.createElement("div");
  swiperEl.className = "swiper";

  const wrapper = document.createElement("div");
  wrapper.className = "swiper-wrapper";
  wrapper.id = container.id || "";
  wrapper.dataset.blockCount = container.dataset.blockCount || String(cardCount);

  while (container.firstElementChild) {
    const slide = document.createElement("div");
    slide.className = "swiper-slide";
    slide.appendChild(container.firstElementChild);
    wrapper.appendChild(slide);
  }

  swiperEl.appendChild(wrapper);
  swiperSlider.appendChild(swiperEl);
  container.parentNode.replaceChild(swiperSlider, container);

  return swiperSlider;
}

/**
 * Binds navigation arrows and counter
 * @param {{ slides: string | any[]; realIndex: any; on: (arg0: string, arg1: () => void) => void; slidePrev: () => any; slideNext: () => any; }} swiper
 * @param {{ querySelector: (arg0: any) => any; }} sectionRoot
 * @param {{ progress: any; counter: any; prev: any; next: any; }} selectors
 */
function bindBannerCollectionPagination(swiper, sectionRoot, selectors) {

  const progressEl = sectionRoot.querySelector(selectors.progress);
  const counterEl = sectionRoot.querySelector(selectors.counter);
  const prevBtn = sectionRoot.querySelector(selectors.prev);
  const nextBtn = sectionRoot.querySelector(selectors.next);

  const update = () => {
    const visibleSlides = Array.from(swiper.slides || []).filter(sl => sl.style.display !== "none");
    const total = visibleSlides.length;
    let currentIndex = swiper.realIndex; 
    let current;
    if (swiper.isEnd) {
        current = total;
    } else {
        const spv = swiper.params.slidesPerView
        current = Math.min(Math.floor(currentIndex + spv), total);
    }
    if (total === 0) current = 0;
    const pct = total > 0 ? (current / total) * 100 : 0;

    if (progressEl) progressEl.style.width = `${pct}%`;
    if (counterEl) {
      counterEl.textContent = `${String(current).padStart(2, "0")}/${String(total).padStart(2, "0")}`;
    }
  };

  update();
  swiper.on("slideChange transitionEnd", update);

  if (prevBtn) prevBtn.addEventListener("click", () => swiper.slidePrev());
  if (nextBtn) nextBtn.addEventListener("click", () => swiper.slideNext());
}

/**
 * Initialize all product carousels
 * @param {{ querySelector: (arg0: string) => any; }} section
 */
function initBannerCollectionCarousels(section) {
  const cardsContainer = section.querySelector("[data-vp-collection-product-cards]");
  if (!cardsContainer) return;

  const swiperSlider = buildBannerCollectionSwiper(
    cardsContainer,
    "vp-product-card-sm__slider vp-width-row-full",
    "vp-banner-collection-product-carousel"
  );

  if (!swiperSlider) return;

  waitForSwiper(swiperSlider, (swiperInstance) => {
    const sectionRoot = swiperSlider.closest("[id^='shopify-section-']");

    bindBannerCollectionPagination(swiperInstance, sectionRoot, {
        progress: ".vp-banner-collection-product-carousel__pagination-progress",
        counter: ".vp-banner-collection-product-carousel__pagination-counter",
        prev: "[data-vp-banner-collection-product-carousel-prev]",
        next: "[data-vp-banner-collection-product-carousel-next]",
    });
  });
}

if (document.readyState === "loading") {
  document.querySelectorAll(".vp-collection-products-slider").forEach(initBannerCollectionCarousels);
} else {
  document.querySelectorAll(".vp-collection-products-slider").forEach(initBannerCollectionCarousels);
}

document.addEventListener("shopify:section:load", (e) => {
  if (e.target.querySelector(".vp-collection-product-cards")) document.querySelectorAll(".vp-collection-products-slider").forEach(initBannerCollectionCarousels);
});